import { Component, ViewChild, ElementRef } from "@angular/core";
import { Router } from "@angular/router";
import { User } from "../../services/User";
import { Locations } from "../../services/Locations";
import { Distributors } from "../../services/Distributors";
import { Session } from "../../services/Session";
import { ISignupWithPA } from "../../interfaces/ISignupWithPA";
import { IDistributor } from "../../interfaces/IDistributor";
import { Notify } from "../../services/Notify";
import { Config } from "../../services/Config";
import { Helpers } from "../../services/Helpers";
import { UntypedFormBuilder, Validators } from "@angular/forms";

@Component({
  selector: "signup",
  templateUrl: "./signup.html",
})
export class SignupCmp {
  public signup = new ISignupWithPA();
  public currentDate = "";
  public distributorsMap: { [id: number]: IDistributor } = {};
  public distributorsList: IDistributor[] = [];
  public saving = false;
  public isError = false;
  public errMsg = "";
  public notify;

  @ViewChild("elEmail") elEmail: ElementRef;
  @ViewChild("submitForm") submitForm: ElementRef;

  key = "6LfbrsEaAAAAAKvnSx_zrBfqVLQQS2BgnlZ9nIqV";

  aFormGroup = this.formBuilder.group({
    captcha: ["", Validators.required],
  });

  // Validation patterns matching VALIDATION_RULES
  private readonly validationPatterns = {
    fname: /^[A-Za-z\- ]{1,120}$/,
    lname: /^[A-Za-z\- ]{1,120}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/,
    facility_name: /^[A-Za-z0-9\- ]{1,300}$/,
    address: /^.{5,70}$/,
    state: /.+/, // Just requires non-empty
    zip: /^\d{5}(-\d{4})?$/,
  };

  constructor(
    private users: User,
    private locations: Locations,
    private distributors: Distributors,
    private router: Router,
    public session: Session,
    private config: Config,
    private h: Helpers,
    private formBuilder: UntypedFormBuilder,
  ) {
    this.h.currentTimeFormattedStringTicker((t) => {
      this.currentDate = t;
    });
    this.distributors.loadToList(this.distributorsList);
    this.notify = new Notify();
  }

  handleReset() { }
  handleExpire() { }
  handleLoad() { }
  handleSuccess($event) { }

  onVerify(token: string) {
    this.signup.captcha_token = token;
  }

  get captcha() {
    return this.aFormGroup.get("captcha");
  }

  submit() {
    // Validate First Name
    if (!this.signup.user.fname || !this.signup.user.fname.trim()) {
      this.notify.error("First Name is required.");
      return;
    }
    if (!this.validationPatterns.fname.test(this.signup.user.fname)) {
      this.notify.error("First Name can contain only letters, spaces or hyphen.");
      return;
    }
    if (this.signup.user.fname.length > 120) {
      this.notify.error("First Name can contain up to 120 characters.");
      return;
    }

    // Validate Last Name
    if (!this.signup.user.lname || !this.signup.user.lname.trim()) {
      this.notify.error("Last Name is required.");
      return;
    }
    if (!this.validationPatterns.lname.test(this.signup.user.lname)) {
      this.notify.error("Last Name can contain only letters, spaces or hyphen.");
      return;
    }
    if (this.signup.user.lname.length > 120) {
      this.notify.error("Last Name can contain up to 120 characters.");
      return;
    }

    // Validate Email
    if (!this.signup.user.email) {
      if (this.elEmail && this.elEmail.nativeElement) {
        try {
          this.signup.user.email = this.elEmail.nativeElement.value;
        } catch (e) { }
      }
    }
    if (!this.signup.user.email || !this.signup.user.email.trim()) {
      this.notify.error("Email is required.");
      return;
    }
    this.signup.user.email = this.signup.user.email.trim();
    if (!this.validationPatterns.email.test(this.signup.user.email)) {
      this.notify.error("Please enter a valid email address (e.g. name@example.com).");
      return;
    }

    // Validate Phone
    if (!this.signup.user.phone || !this.signup.user.phone.trim()) {
      this.notify.error("Phone is required.");
      return;
    }
    if (!this.validationPatterns.phone.test(this.signup.user.phone)) {
      this.notify.error("Please enter a phone number in the format 123-456-7890.");
      return;
    }

    // Validate Facility Name
    if (!this.signup.location.facility_name || !this.signup.location.facility_name.trim()) {
      this.notify.error("Facility Name is required.");
      return;
    }
    if (!this.validationPatterns.facility_name.test(this.signup.location.facility_name)) {
      this.notify.error("Facility Name can contain only letters, numbers, spaces or hyphen.");
      return;
    }
    if (this.signup.location.facility_name.length > 300) {
      this.notify.error("Facility Name can contain up to 300 characters.");
      return;
    }

    // Validate Address
    if (!this.signup.location.address || !this.signup.location.address.trim()) {
      this.notify.error("Address is required.");
      return;
    }
    if (!this.validationPatterns.address.test(this.signup.location.address)) {
      this.notify.error("Address can contain from 5 to 70 characters.");
      return;
    }

    // Validate State
    if (!this.signup.location.state || !this.signup.location.state.trim()) {
      this.notify.error("State is required.");
      return;
    }

    // Validate Zip
    if (!this.signup.location.zip || !this.signup.location.zip.trim()) {
      this.notify.error("Zip is required.");
      return;
    }
    if (!this.validationPatterns.zip.test(this.signup.location.zip)) {
      this.notify.error("Zip must be in the format XXXXX or XXXXX-XXXX.");
      return;
    }

    // Validate Terms of Service agreement
    if (!this.signup.signature) {
      this.notify.error("Agreement to Terms of Service, Privacy Policy and Participation legal document is required.");
      return;
    }

    // Validate Captcha
    if (this.captcha.invalid) {
      this.notify.error("Please complete the captcha verification.");
      return;
    }

    this.trySignup();
  }

  trySignup() {
    let theme_id = this.config.getThemeIDForHost();
    this.signup.user.theme_id = theme_id;
    this.signup.location.theme_id = theme_id;
    if (theme_id == this.config.defaultThemeID) {
      this.signup.to_generate_rx = true;
    }

    this.saving = true;
    this.users.signupWithPA(this.signup).subscribe(
      (response) => {
        if (
          theme_id == this.config.defaultThemeID &&
          response &&
          response.token &&
          response.rx_code
        ) {
          this.saving = false;
          this.session.signupUrl = window.location.hostname + window.location.pathname;
          this.router.navigateByUrl(
            `/new_member?msg_id=1&code=${response.token}&ask_vac=1&rx=${response.rx_code}`,
          );
        } else {
          this.session.setToken(
            response.token,
            () => {
              this.saving = false;
              this.session.signupUrl = window.location.hostname + window.location.pathname;
              this.router.navigate(["/signup_benefit_review"]);
            },
            () => {
              this.saving = false;
              this.router.navigate(["/login"]);
            },
          );
        }
      },
      (response) => {
        this.isError = true;
        this.saving = false;
        console.error(response);
        if (response && response.status) {
          switch (response.status) {
            case 417:
              this.errMsg = "Please double-check the name that you entered. There appears to be a typo.";
              break;
            case 409:
              this.errMsg = "User with this email is already registered.";
              break;
            case 406:
              this.errMsg = "Please enter a valid email address (e.g. name@example.com).";
              break;
            case 400:
              this.errMsg = "Please fill all required fields correctly.";
              break;
            case 418:
              this.errMsg = "Please enter a phone number in the format 123-456-7890.";
              break;
            case 419:
              this.errMsg = "Zip must be in the format XXXXX or XXXXX-XXXX.";
              this.signup.location.zip = null;
              break;
            default:
              this.errMsg = "";
          }
          if (this.errMsg) {
            this.notify.error(this.errMsg);
          }
        }
      },
    );
  }
}
